<!-- eslint-disable vue/valid-v-model -->
<template>
  <div>
    <ValidationObserver
      ref="validationObserver"
      v-slot="{ handleSubmit }"
    >
      <form
        class="contact-info"
        novalidate="novalidate"
        @submit.prevent="handleSubmit(submit)"
        @input="handleFormChange"
      >
        <div class="contact-info">
          <div
            v-if="hasChanges && shouldShowWarning"
            class="warning-message"
          >
            <inline-svg
              :src="iconWarning"
              class="warning-icon"
            ></inline-svg>
            Changes won’t be saved unless you click Apply.
          </div>
          <div class="inline-row">
            <MaterialInput
              v-model="formData.title"
              v-bind="formFields.title"
              :requiredFromBack="true"
            />
            <MaterialInput
              v-model="formData.shortDescription"
              v-bind="formFields.shortDescription"
            />
            <MaterialTextArea
              v-model="formData.longDescription"
              v-bind="formFields.longDescription"
            />
            <div
              v-if="collectionType !== 'season'"
              class="categories-management-wrapper"
            >
              <CategoriesManagement
                v-if="isCategoryManagementAvailabe"
                label="Select Categories"
                :selectedValue="formData.categories"
                :onChange="handleCategoryChange"
              />
            </div>
            <div
              v-if="collectionType !== 'season'"
              class="genres-management-wrapper"
            >
              <GenresManagement
                v-if="isGenreManagementAvailabe"
                label="Select Genres"
                :selectedValue="formData.genres"
                :onChange="handleGenreChange"
              />
            </div>
            <div>
              <div class="localization-btn">
                <Button
                  title="Localization"
                  classType="primary"
                  className="wide-btn"
                  :srcSvg="iconLocalization"
                  svgClass="folder-localization-icon"
                  :eventFunction="handleOpenLocalizationModal"
                />
              </div>
            </div>
            <div
              v-if="hasSortedFields() && isFormInited"
              class="custom-field-wrapper"
            >
              <div class="custom-field-title">
                Custom fields
              </div>
              <FieldValueRow
                v-for="item in getSortedFields()"
                :key="`fieldValue${item.field.id}`"
                v-model="item.fieldValue"
                :field="item.field"
                :fieldName="getFieldName(item.field)"
                :formPrefix="formPrefix"
              />
            </div>
            <div class="action-buttons-wrapper">
              <Button
                title="Revert"
                classType="warning"
                className="without-margin"
                :eventFunction="handleRevertForm"
              />
              <Button
                title="Apply"
                classType="primary"
                type="submit"
                className="without-margin"
              />
            </div>
          </div>
        </div>
      </form>
    </ValidationObserver>
    <CollectionLocalization
      v-if="isLocalizationOpen"
      :metadata="formData"
      :type="collectionType"
      collectionTab="general"
      :onClose="handleCloseLocalizationModal"
    />
  </div>
</template>

<script>
import {
  mapGetters,
} from 'vuex';
import CONFIG from '../../constants/config';
import {
  SET_EDIT_FORM,
} from '../../store/actions/common';

import iconFolderLocalization from '../../assets/icons/icon-folder-localization.svg';

import Button from '../common/Button.vue';
import BaseForm from './BaseForm.vue';
import MaterialInput from '../inputs/MaterialInput.vue';
import MaterialTextArea from '../inputs/MaterialTextArea.vue';
import CustomFieldFormMixin from './customFields/CustomFieldFormMixin.vue';
import CollectionLocalization from '../localization/CollectionLocalization.vue';
import iconWarning from '../../assets/icons/icon-warning-localization.svg';
import CategoriesManagement from '../inputs/CategoriesManagement.vue';
import GenresManagement from '../inputs/GenresManagement.vue';

export default {
  name: 'CollectionGeneralForm',
  components: {
    CollectionLocalization,
    MaterialInput,
    MaterialTextArea,
    Button,
    CategoriesManagement,
    GenresManagement,
  },
  extends: BaseForm,
  mixins: [
    CustomFieldFormMixin,
  ],
  props: {
    formPrefix: {
      type: String,
      default: 'playlistEditForm',
    },
    collectionType: {
      type: String,
      default: '',
    },
    isCategoryManagementAvailabe: {
      type: Boolean,
      default: false,
    },
    isGenreManagementAvailabe: {
      type: Boolean,
      default: false,
    },
    metadata: {
      type: Object,
      default: () => {},
    },
    enabledLanguages: {
      type: Array,
      default: () => [],
    },
    submitEventName: {
      type: String,
      required: true,
    },
    submitParams: {
      type: Object,
      default: () => ({}),
    },
    successMessageText: {
      type: String,
      default: '',
    },
    hasChanges: {
      type: Boolean,
      default: false,
    },
    setHasChanges: {
      type: Function,
      default: () => {},
    },
    shouldShowWarning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iconWarning,
      successMessage: this.successMessageText,
      submitEvent: this.submitEventName,
      iconLocalization: iconFolderLocalization,
      isLocalizationOpen: false,
      errorMessage: CONFIG.errorMessages.commonServerError,
      isFormInited: false,
      formData: {
        title: '',
        shortDescription: '',
        longDescription: '',
        customFields: [],
        categories: [],
        genres: [],
      },
      formFields: {
        title: {
          type: 'text',
          name: 'title',
          formPrefix: this.formPrefix,
          rules: {
            required: false,
            max: 255,
          },
        },
        shortDescription: {
          type: 'text',
          name: 'shortDescription',
          formPrefix: this.formPrefix,
          rules: {
            required: false,
            max: 255,
          },
        },
        longDescription: {
          type: 'text',
          name: 'longDescription',
          cols: 30,
          rows: 10,
          formPrefix: this.formPrefix,
          rules: {
            required: false,
            max: 1800,
          },
        },
        sortDirection: {
          type: 'text',
          name: 'sortDirection',
          formPrefix: this.formPrefix,
          rules: {
            required: false,
          },
        },
      },
      errorMapping: {
        title: [
          `${this.formPrefix}.title`,
        ],
        shortDescription: [
          `${this.formPrefix}.shortDescription`,
        ],
        longDescription: [
          `${this.formPrefix}.longDescription`,
        ],
      },
    };
  },
  computed: {
    ...mapGetters([
      'getCurrentAccountId',
    ]),
  },
  mounted() {
    this.initFormData();
  },
  methods: {
    initFormData() {
      this.formData.id = this.metadata.id;
      this.formData.title = this.metadata.title ? this.metadata.title : this.folderName;
      this.formData.shortDescription = this.metadata.shortDescription;
      this.formData.longDescription = this.metadata.longDescription;
      if (this.collectionType !== 'season') {
        this.formData.categories = this.metadata.categories;
        this.formData.genres = this.metadata.genres;
      }

      this.formData.customFields = JSON.parse(JSON.stringify(this.metadata.customFields));
      this.setCustomFields(this.formData.customFields);

      this.isFormInited = true;
    },
    handleFormChange() {
      this.$store.dispatch(SET_EDIT_FORM, true);
      this.setHasChanges(true);
    },
    handleCategoryChange(value) {
      this.formData.categories = value;
      this.$store.dispatch(SET_EDIT_FORM, true);
      this.setHasChanges(true);
    },
    handleGenreChange(value) {
      this.formData.genres = value;
      this.$store.dispatch(SET_EDIT_FORM, true);
      this.setHasChanges(true);
    },
    handleOpenLocalizationModal() {
      this.isLocalizationOpen = true;
    },
    handleCloseLocalizationModal() {
      this.isLocalizationOpen = false;
    },
    handleRevertForm() {
      this.$store.dispatch(SET_EDIT_FORM, false);
      this.setHasChanges(false);
      this.$emit('resetShouldShowWarning');

      this.$nextTick(() => {
        this.initFormData();
      });
    },
    getRequestData() {
      const { formData } = this;
      const { submitParams } = this;
      const data = {
        title: formData.title,
        shortDescription: formData.shortDescription,
        longDescription: formData.longDescription,
      };

      data.fields = this.getFieldsData();

      if (this.collectionType !== 'season') {
        data.categories = formData.categories.map((item) => item.id);
        data.genres = formData.genres.map((item) => item.id);
      }

      submitParams.data = data;

      return submitParams;
    },
    onSubmitFail(error) {
      if (error.form_errors?.children && error.form_errors.children.title) {
        this.tabErrors.general = true;
      }

      this.formErrors = this.getServerFormErrors(error);
      this.itemsErrors = this.getServerFormCollectionErrors(error);

      if (Object.entries(this.formErrors).length && this.$refs[this.validationObserver]) {
        this.$refs[this.validationObserver].setErrors(this.formErrors);
      } else {
        this.$toasted.global.error({
          message: this.errorMessage,
        });
      }
    },
    onSubmitSuccess(res) {
      this.$toasted.global.success({
        message: this.successMessage,
      });
      if (res.data?.id === this.metadata.id) {
        this.$emit('handleUpdatedMetadata', res.data);
      } else {
        this.$emit('reloadItem');
      }
      this.$store.dispatch(SET_EDIT_FORM, false);
      this.setHasChanges(false);
      this.$emit('resetShouldShowWarning');
    },
  },
};
</script>

<style scoped>
  .person p:last-child {
    background: #383b40;
    margin-top: 10px;
    padding: 15px;
  }

  .wrapper-small {
    margin-bottom: 20px;
  }

  hr {
    border: 0 solid rgba(0, 0, 0, 0.12);
    border-top-width: thin;
    display: block;
    flex: 1 1 0;
    height: 0;
    max-height: 0;
    max-width: 100%;
    transition: inherit;
  }

  .custom-field-wrapper {
    margin-top: 20px;
    overflow-wrap: initial;
    white-space: initial;
  }

  .custom-field-title {
    font-size: 16px;
    margin-bottom: 20px;
  }

  .wide-btn {
    width: 100%;
    margin-left: 0;
  }

  .categories-management-wrapper {
    margin-bottom: 20px;
    margin-top: 20px;
  }

  .genres-management-wrapper {
    margin-bottom: 20px;
    margin-top: 20px;
  }

  .action-buttons-wrapper {
    display: flex;
    justify-content: flex-start;
    gap: 10px;
    margin-top: 20px;
  }

  .warning-message {
    color: #F2C94C;
    gap: 6px;
    display: flex;
    align-items: center;
    padding: 8px 10px;
    border: 1px solid #F2C94C;
    border-radius: 4px;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    width: fit-content;
    margin-bottom: 30px;
  }
</style>
